"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Calculator, CircleCheck as CheckCircle2, Ruler } from 'lucide-react';
export default function CostFactorsIntro() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  return <section id="costfactorsintro" data-nav="light" ref={ref} className="bg-background text-foreground py-20 md:py-28 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div style={{
        opacity: isVisible ? 1 : 0
      }} className="transition-all duration-700 ease-out transform">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Narrative & Key Scope Drivers */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <span className="w-8 h-px bg-primary inline-block" />
                Planning Your Investment
              </div>

              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15]">
                Remodeling costs reflect your scope, not a flat checklist.
              </h1>

              <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-default">
                Every residential home in Brookhaven brings unique architectural conditions, structural layouts, and owner ambitions. Because variables differ significantly from property to property, this guide outlines the physical and design factors that dictate cost—rather than arbitrary price tags.
              </p>

              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Whether considering load-bearing wall modifications, rerouting plumbing lines, or selecting custom hardwood cabinetry, total expenditure is shaped by structural adjustments and finish tiers. An accurate, reliable budget always begins with a detailed, personalized in-home consultation.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border">
                  <div className="p-2 rounded-md bg-accent text-accent-foreground shrink-0">
                    <Ruler className="size-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-sm text-card-foreground">
                      Layout and Structural Changes
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Moving walls, structural beams, and HVAC or plumbing runs.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border">
                  <div className="p-2 rounded-md bg-accent text-accent-foreground shrink-0">
                    <Calculator className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-card-foreground">
                      Material and Finish Specifications
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Custom cabinetry, natural stone countertops, and architectural millwork.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Button size="lg" asChild>
                  <Link to="/ContactRequestAnEstimatePage">
                    Schedule a Consultation
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Personalized quotes provided during design discovery</span>
                </div>
              </div>
            </div>

            {/* Right Column: Realistic Imagery with Consultation Callout */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden border border-border shadow-xl bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_detail.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_detail.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_detail.webp-wvc-sizes" alt="Open-concept residential kitchen and living space showing tailored custom cabinetry and stone island" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 lg:h-[460px] object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/30 to-transparent flex flex-col justify-end p-6 sm:p-8">
                  <Card className="bg-background/95 backdrop-blur-sm border-border text-foreground shadow-lg py-4">
                    <CardContent className="px-5 py-0 flex flex-col gap-2">
                      <span className="text-xs font-semibold tracking-wide uppercase text-primary">
                        Site-Specific Estimating
                      </span>
                      <p className="text-sm font-medium leading-snug">
                        Ready to establish a realistic budget for your home? We inspect on-site conditions before preparing final costs.
                      </p>
                      <div className="pt-1">
                        <Link to="/ContactRequestAnEstimatePage" className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                          Request an in-home assessment
                          <ArrowRight className="size-3" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}